/**
 * Two columns with a draggable split: a fluid main column and an aside whose
 * width the viewer sets by dragging the handle between them, or with the
 * arrow keys on it. The width is a per-viewer convenience kept in
 * localStorage; below the `lg` breakpoint the columns stack and the handle
 * is not drawn.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export const ASIDE_MIN = 280;
export const ASIDE_MAX = 760;
export const ASIDE_DEFAULT = 320;
const KEY_STEP = 16;

/** Keep an aside width inside the range the layout can show. */
export function clampAsideWidth(width: number): number {
  if (!Number.isFinite(width)) return ASIDE_DEFAULT;
  return Math.min(ASIDE_MAX, Math.max(ASIDE_MIN, Math.round(width)));
}

function readStored(storageKey: string): number {
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw === null ? ASIDE_DEFAULT : clampAsideWidth(Number(raw));
  } catch {
    return ASIDE_DEFAULT;
  }
}

function writeStored(storageKey: string, width: number): void {
  try {
    window.localStorage.setItem(storageKey, String(width));
  } catch {
    // Private windows and blocked storage fall back to the session's value.
  }
}

interface Props {
  /** The localStorage key that remembers the aside width for this page. */
  storageKey: string;
  main: ReactNode;
  aside: ReactNode;
}

export function ResizableColumns({ storageKey, main, aside }: Props) {
  const [width, setWidth] = useState<number>(() => readStored(storageKey));
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const commit = useCallback(
    (next: number) => {
      const clamped = clampAsideWidth(next);
      setWidth(clamped);
      writeStored(storageKey, clamped);
    },
    [storageKey],
  );

  useEffect(() => {
    // The pointer leaves the handle as soon as the drag starts, so the move
    // and release are tracked on the window for the drag's whole duration.
    function onMove(event: PointerEvent) {
      if (!dragging.current || !containerRef.current) return;
      const right = containerRef.current.getBoundingClientRect().right;
      setWidth(clampAsideWidth(right - event.clientX));
    }
    function onUp(event: PointerEvent) {
      if (!dragging.current || !containerRef.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      const right = containerRef.current.getBoundingClientRect().right;
      commit(right - event.clientX);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [commit]);

  const style = { '--aside-w': `${width}px` } as CSSProperties;

  return (
    <div
      ref={containerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) var(--aside-w)',
        gap: 22,
        ...style,
      }}
    >
      <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 22 }}>{main}</div>
      <aside
        style={{
          position: 'relative',
          display: 'flex',
          minWidth: 0,
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the environment column"
          aria-valuemin={ASIDE_MIN}
          aria-valuemax={ASIDE_MAX}
          aria-valuenow={width}
          tabIndex={0}
          title="Drag to resize · double-click to reset"
          onPointerDown={(e) => {
            e.preventDefault();
            dragging.current = true;
            document.body.style.cursor = 'col-resize';
          }}
          onDoubleClick={() => commit(ASIDE_DEFAULT)}
          onKeyDown={(e) => {
            // Left widens the aside (the handle moves left), right narrows it.
            if (e.key === 'ArrowLeft') commit(width + KEY_STEP);
            else if (e.key === 'ArrowRight') commit(width - KEY_STEP);
            else if (e.key === 'Home') commit(ASIDE_DEFAULT);
          }}
          style={{
            position: 'absolute',
            left: -20,
            top: 0,
            display: 'block',
            height: '100%',
            width: 16,
          }}
        >
          <span
            style={{
              marginInline: 'auto',
              display: 'block',
              height: '100%',
              width: 1,
              background: 'var(--border-hairline)',
            }}
          />
        </div>
        {aside}
      </aside>
    </div>
  );
}
